// ABOUTME: Hook to read/write the current user's default configuration characteristic values.
// ABOUTME: Wraps useUserSettings to expose the configuratorDefaults field with merge-preserving writes.

import { useCallback, useMemo } from 'react';

import { useUserSettings } from '../../settings/hooks/useSettingsApi';

// Flat storage shape for a single default characteristic value. Mirrors the server
// UserSettingsSchema.configuratorDefaults entry. The nested configurator TUserAssignment
// shape is converted to/from this at the configurator boundary.
export type ConfiguratorDefault = {
    featureCode: string;
    optionCode: string;
};

type UserSettingsData = Record<string, unknown> & {
    configuratorDefaults?: ConfiguratorDefault[];
};

/**
 * Reads and persists the user's configurator defaults stored under the
 * `configuratorDefaults` field of the user settings document. Writes preserve
 * any other user-settings fields (e.g. email).
 */
export function useConfiguratorDefaults() {
    const { data, loading, error, update, reset } = useUserSettings();

    const settingsData = data as UserSettingsData | null;

    const defaults = useMemo<ConfiguratorDefault[]>(
        () => settingsData?.configuratorDefaults ?? [],
        [settingsData],
    );

    const saveDefaults = useCallback(
        async (assignments: ConfiguratorDefault[]): Promise<boolean> => {
            const next: UserSettingsData = {
                ...(settingsData ?? {}),
                configuratorDefaults: assignments,
            };
            return update(next);
        },
        [settingsData, update],
    );

    const clearDefault = useCallback(
        async (featureCode: string, optionCode: string): Promise<boolean> => {
            const next = defaults.filter(
                assignment =>
                    !(
                        assignment.featureCode === featureCode &&
                        assignment.optionCode === optionCode
                    ),
            );
            return saveDefaults(next);
        },
        [defaults, saveDefaults],
    );

    return {
        defaults,
        loading,
        error,
        saveDefaults,
        clearDefault,
        reset,
    };
}
